/**
 * Lets a page temporarily hide GlobalChrome's floating corner buttons (back,
 * jump-to-top, assistant launcher) while something on the page needs the
 * whole viewport for its own scroll surface and supplies its own way back.
 * Full-screen schedule and conversation surfaces use this to drop the FAB
 * band's body-padding reservation and reclaim the whole viewport. A hidden
 * reservation with visible FABs would cover the surface's own bottom rows;
 * hiding both together removes the overlap instead of guessing at clearance.
 *
 * In-memory only, not persisted: this is a per-mount request tied to a
 * page being on screen, not a saved preference.
 */
import { useEffect, useSyncExternalStore } from 'react';

let hideCount = 0;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): boolean {
  return hideCount > 0;
}

/** GlobalChrome reads this to decide whether to render its floating buttons. */
export function useGlobalFabsHidden(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Call with `hidden=true` while a page wants the floating buttons gone.
 * Ref-counted so two simultaneous requesters cannot let one's cleanup show
 * the buttons out from under the other. Always restores on unmount,
 * including an unmount caused by a
 * browser Back navigating away while still requesting - and on `hidden`
 * flipping back to false.
 */
export function useRequestFabsHidden(hidden: boolean): void {
  useEffect(() => {
    if (!hidden) return;
    hideCount += 1;
    notify();
    return () => {
      hideCount = Math.max(0, hideCount - 1);
      notify();
    };
  }, [hidden]);
}
